import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { completeUploads, createUpload, invalidatePhotoData, useAlbums } from '../../api/hooks';
import { AlbumCombobox, type AlbumValue } from '../../components/AlbumPicker';
import { Icon } from '../../components/Icon';
import { TagEditor } from '../../components/TagEditor';
import { filesFromDrop, mapLimit, readTakenAt, sha256File, unzipPhotos } from '../../lib/files';
import { fmtSize } from '../../lib/format';
import {
  RENDERABLE_RE,
  SUPPORTED_RE,
  applyUploadResponse,
  uploadBody,
  captureYears,
  contentTypeFor,
  extOf,
  initialAutoTags,
  isZip,
  itemYear,
  markLocalDuplicates,
  planAutoTags,
  queueStats,
  type AutoTagState,
  type UploadItem,
} from '../../lib/uploadPlan';
import { putWithRetry } from '../../lib/uploader';
import { regionTag, type CreateUploadBody } from '@photos/shared';
import { VISIBILITY_LOOK } from '../../lib/visibility';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { NoAccess } from '../NoAccess';
import { NewAlbumDialog } from './AlbumsPage';

const SUGGESTED = [
  'summer-retreat',
  'winter-retreat',
  'young-adult-retreat',
  'officers-retreat',
  'yg-summer-camp',
  'worship',
  'group-photo',
];
const UPLOAD_CONCURRENCY = 5;
const READ_CONCURRENCY = 4;
const COMPLETE_CHUNK = 100;

type Phase = 'idle' | 'uploading' | 'done';

let seq = 0;
const newClientId = () => `c${Date.now().toString(36)}${(seq++).toString(36)}`;

/**
 * `/studio/upload` (and "Upload to this album" → `?album=<id>`): the album is required — an existing one the caller can
 * upload to, or "+ New album" (media). Tags work as before: per-photo capture year, the uploader's region and
 * one-click profile chips.
 */
export function UploadPage() {
  const { user, access, tagIndex } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const year = String(new Date().getFullYear());
  const [params] = useSearchParams();
  const albums = useAlbums();
  const uploadable = useMemo(() => (albums.data?.items ?? []).filter((a) => access.canUploadTo(a.id)), [albums.data, access]);

  const [items, setItems] = useState<UploadItem[]>([]);
  const [tagState, setTagState] = useState<AutoTagState>(() => initialAutoTags(year, user.region && regionTag(user.region)));
  const [album, setAlbum] = useState<AlbumValue>(null);
  // "Upload to this album" preselects the album once the list is loaded.
  const albumParam = params.get('album');
  useEffect(() => {
    if (album || !albumParam) return;
    const a = uploadable.find((x) => x.id === albumParam);
    if (a) setAlbum({ id: a.id, name: a.name });
  }, [albumParam, uploadable, album]);
  // "+ New album": a small step to pick the new album's default tags (or skip) before uploading into it.
  const [newAlbumName, setNewAlbumName] = useState<string | null>(null);
  const chooseAlbum = (v: AlbumValue) => {
    if (v && 'newName' in v) setNewAlbumName(v.newName);
    else setAlbum(v);
  };
  // The chosen album's default tags start as removable chips marked "album"; switching albums swaps them.
  const albumDefaults = useMemo(
    () => (album && 'id' in album ? (uploadable.find((a) => a.id === album.id)?.defaultTags ?? []) : []),
    [album, uploadable],
  );
  const albumDefaultsKey = albumDefaults.join(',');
  const prevDefaults = useRef<string[]>([]);
  useEffect(() => {
    const prev = prevDefaults.current;
    const next = albumDefaultsKey ? albumDefaultsKey.split(',') : [];
    prevDefaults.current = next;
    // Auto tags (capture year, the uploader's region) stay even when the previous album had them too.
    setTagState((s) => ({ ...s, tags: [...new Set([...s.tags.filter((t) => !prev.includes(t) || next.includes(t) || s.auto.includes(t)), ...next])] }));
  }, [albumDefaultsKey]);
  const [uploadedTo, setUploadedTo] = useState<{ id: string; name: string } | null>(null);
  const uploadedToRef = useRef(uploadedTo);
  uploadedToRef.current = uploadedTo;
  const visibilities = access.uploadVisibilities(album && 'id' in album ? album.id : '') as CreateUploadBody['visibility'][];
  const [visibility, setVisibility] = useState<CreateUploadBody['visibility']>('unreleased');
  // One-click tags from the uploader's profile (church, fellowship group, service groups), hidden once added.
  const profileTags = useMemo(
    () => [user.church, user.fellowship ?? '', ...user.services].filter(Boolean),
    [user.church, user.fellowship, user.services],
  );
  const [phase, setPhase] = useState<Phase>('idle');
  const [over, setOver] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Progress events are frequent: collect them in a ref and flush once per frame.
  const progress = useRef(new Map<string, number>());
  const flushRaf = useRef(0);
  const scheduleFlush = useCallback(() => {
    if (flushRaf.current) return;
    flushRaf.current = requestAnimationFrame(() => {
      flushRaf.current = 0;
      const p = progress.current;
      progress.current = new Map();
      setItems((l) => l.map((it) => (p.has(it.clientId) ? { ...it, progress: p.get(it.clientId)! } : it)));
    });
  }, []);

  const patch = useCallback((clientId: string, change: Partial<UploadItem>) => {
    setItems((l) => l.map((it) => (it.clientId === clientId ? { ...it, ...change } : it)));
  }, []);

  // Release preview URLs when leaving the page.
  useEffect(
    () => () => {
      for (const it of itemsRef.current) if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      cancelAnimationFrame(flushRaf.current);
    },
    [],
  );

  // Auto year tags follow the capture years of the queued files.
  const yearsKey = captureYears(items).join(',');
  useEffect(() => {
    setTagState((s) => planAutoTags(s, yearsKey ? yearsKey.split(',') : [], year));
  }, [yearsKey, year]);

  const addFiles = async (input: File[]) => {
    if (phase !== 'idle') return;
    const supported = input.filter((f) => SUPPORTED_RE.test(f.name));
    if (!supported.length) {
      toast({ icon: 'info', title: 'No photos found', sub: 'Supported: JPG, PNG, HEIC, WebP, RAW and ZIP.' });
      return;
    }
    const files: File[] = [];
    for (const f of supported) {
      if (!isZip(f.name)) {
        files.push(f);
        continue;
      }
      const t = toast({ icon: 'zip', title: `Unpacking ${f.name}…`, sub: 'ZIP archives are unpacked in your browser.', progress: 0 });
      try {
        const inner = await unzipPhotos(f);
        files.push(...inner);
        t.update({ title: `${f.name}: ${inner.length} photos found`, sub: 'Duplicates already in Glean are skipped.', progress: 1 });
      } catch (e) {
        t.update({ title: `Couldn’t open ${f.name}`, sub: (e as Error).message, progress: undefined });
      }
      t.close(3500);
    }
    if (!files.length) return;

    const fresh: UploadItem[] = files.map((file) => ({
      clientId: newClientId(),
      file,
      name: file.name,
      bytes: file.size,
      contentType: contentTypeFor(file.name, file.type),
      previewUrl: RENDERABLE_RE.test(file.name) ? URL.createObjectURL(file) : null,
      status: 'reading',
      progress: 0,
    }));
    setItems((l) => [...l, ...fresh]);

    await mapLimit(fresh, READ_CONCURRENCY, async (it) => {
      try {
        const [takenAt, sha256] = await Promise.all([readTakenAt(it.file), sha256File(it.file)]);
        patch(it.clientId, { takenAt, sha256, year: itemYear(takenAt, it.file.lastModified), status: 'ready' });
      } catch (e) {
        patch(it.clientId, { status: 'error', note: `Couldn’t read this file: ${(e as Error).message}` });
      }
    });
    setItems((l) => markLocalDuplicates(l));
  };

  const uploadAll = async (targets: UploadItem[]) => {
    const okIds: string[] = [];
    await mapLimit(targets, UPLOAD_CONCURRENCY, async (it) => {
      if (!it.target || !it.photoId) return;
      patch(it.clientId, { status: 'uploading', progress: 0, note: undefined });
      try {
        await putWithRetry(it.target.url, it.file, it.target.headers, (f) => {
          progress.current.set(it.clientId, f);
          scheduleFlush();
        });
        okIds.push(it.photoId);
        patch(it.clientId, { status: 'done', progress: 1 });
      } catch (e) {
        patch(it.clientId, { status: 'error', note: (e as Error).message });
      }
    });
    for (let i = 0; i < okIds.length; i += COMPLETE_CHUNK) {
      await completeUploads(okIds.slice(i, i + COMPLETE_CHUNK));
    }
    return okIds.length;
  };

  const finish = (uploaded: number, failed: number) => {
    setPhase('done');
    invalidatePhotoData(qc);
    if (uploaded)
      toast({
        icon: 'check',
        title: `Uploaded ${uploaded} photos`,
        sub: `Tagged ${tagState.tags.map((t) => `#${t}`).join(' ')} · they’re in ${uploadedToRef.current?.name ?? 'the album'}, unreleased until someone releases them.`,
      });
    if (failed)
      toast({ icon: 'info', title: `${failed} photos didn’t upload`, sub: 'Check your connection and choose Retry failed.' });
  };

  const start = async () => {
    if (!album) return;
    const body = uploadBody(items, tagState, visibility, 'id' in album ? { albumId: album.id } : { newAlbumName: album.newName });
    if (!body.files.length) return;
    setPhase('uploading');
    try {
      const res = await createUpload(body);
      if (res.albumId && res.albumName) {
        const target = { id: res.albumId, name: res.albumName };
        setAlbum(target);
        setUploadedTo(target);
        uploadedToRef.current = target;
      }
      const next = applyUploadResponse(itemsRef.current, res);
      setItems(next);
      if (res.duplicates.length)
        toast({
          icon: 'info',
          title: `${res.duplicates.length} duplicates skipped`,
          sub: 'They’re already in Glean.',
        });
      const targets = next.filter((i) => i.target && i.status === 'ready');
      const uploaded = await uploadAll(targets);
      finish(uploaded, targets.length - uploaded);
    } catch (e) {
      setPhase('idle');
      toast({ icon: 'info', title: 'Upload failed', sub: (e as Error).message });
    }
  };

  const retryFailed = async () => {
    const failed = itemsRef.current.filter((i) => i.status === 'error' && i.target);
    if (!failed.length) return;
    setPhase('uploading');
    try {
      const uploaded = await uploadAll(failed);
      finish(uploaded, failed.length - uploaded);
    } catch (e) {
      setPhase('done');
      toast({ icon: 'info', title: 'Upload failed', sub: (e as Error).message });
    }
  };

  const clear = () => {
    for (const it of items) if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    setItems([]);
    setPhase('idle');
  };

  const onDrop = async (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    await addFiles(await filesFromDrop(e.dataTransfer));
  };

  if (!access.isMedia && !access.collaboratorAlbumIds.length) return <NoAccess area="Upload" inline />;

  const stats = queueStats(items);
  const reading = items.filter((i) => i.status === 'reading').length;
  const ready = items.filter((i) => i.status === 'ready').length;
  const years = captureYears(items);
  const uploadTotal = items.filter((i) => i.target).length;
  const uploadDone = items.filter((i) => i.target && i.status === 'done').length;
  const overall = uploadTotal ? items.filter((i) => i.target).reduce((a, i) => a + (i.status === 'done' ? 1 : i.progress), 0) / uploadTotal : 0;
  const goLabel =
    phase === 'done'
      ? stats.failed
        ? `Done · ${stats.failed} failed`
        : 'Done ✓'
      : phase === 'uploading'
        ? `Uploading… ${uploadDone}/${uploadTotal}`
        : reading
          ? `Reading ${items.length - reading} of ${items.length}…`
          : items.length && !album
            ? 'Choose an album first'
            : items.length
              ? `Upload ${ready} photos`
              : 'Upload';

  return (
    <div className="wrap">
      <div className="toolbar">
        <h1>Upload photos</h1>
        <span className="meta">Originals are stored at full quality, forever.</span>
      </div>
      <div className="up">
        <div>
          <div
            className={`drop${over ? ' over' : ''}`}
            id="drop"
            onDragEnter={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setOver(false);
            }}
            onDrop={onDrop}
          >
            <div className="big">
              <Icon name="upload" />
            </div>
            <h2>Drop a folder, a ZIP, or photos here</h2>
            <p>Hundreds of files at once is fine. Uploads retry automatically if your connection drops.</p>
            <div className="row">
              <label className="btn primary">
                <Icon name="folder" />
                Choose folder
                <input
                  type="file"
                  multiple
                  hidden
                  id="fFolder"
                  disabled={phase !== 'idle'}
                  ref={(el) => el?.setAttribute('webkitdirectory', '')}
                  onChange={(e) => {
                    void addFiles([...(e.target.files ?? [])]);
                    e.target.value = '';
                  }}
                />
              </label>
              <label className="btn">
                <Icon name="image" />
                Choose files
                <input
                  type="file"
                  accept="image/*,.heic,.heif,.cr2,.cr3,.nef,.arw,.dng,.zip"
                  multiple
                  hidden
                  id="fFiles"
                  disabled={phase !== 'idle'}
                  onChange={(e) => {
                    void addFiles([...(e.target.files ?? [])]);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <div className="formats">JPG · PNG · HEIC · WebP · RAW (CR3, NEF, ARW, DNG) · ZIP archives are unpacked in your browser</div>
          </div>
          {items.length > 0 && (
            <div className="queue" id="queue">
              <div className="qhead">
                <b>{stats.total} photos</b>
                <div className="stats">
                  <span>
                    <b>{stats.todo}</b> to upload
                  </span>
                  <span>
                    <b>{stats.dups}</b> duplicates skipped
                  </span>
                  <span>
                    <b>{fmtSize(stats.bytes)}</b>
                  </span>
                  <span>
                    Capture year: <b>{years.join(', ') || '–'}</b> (auto-tagged)
                  </span>
                </div>
                <span style={{ flex: 1 }} />
                {phase !== 'uploading' && (
                  <button type="button" className="btn sm ghost" id="clr" onClick={clear}>
                    {phase === 'done' ? 'Upload more' : 'Clear'}
                  </button>
                )}
                {phase === 'done' && stats.failed > 0 && (
                  <button type="button" className="btn sm" onClick={retryFailed}>
                    Retry failed
                  </button>
                )}
                {phase === 'done' && uploadDone > 0 && uploadedTo && (
                  <button
                    type="button"
                    className="btn sm primary"
                    onClick={() => {
                      window.scrollTo(0, 0);
                      navigate(`/studio/albums/${uploadedTo.id}`);
                    }}
                  >
                    Go to album →
                  </button>
                )}
                <div className="bar" style={{ flexBasis: '100%' }} id="tot">
                  <i style={{ width: `${Math.round(overall * 100)}%` }} />
                </div>
              </div>
              <div className="qgrid">
                {items.map((f) => {
                  const dup = f.status === 'dup';
                  const done = f.status === 'done';
                  const err = f.status === 'error';
                  return (
                    <div className={`qi${dup ? ' dup' : ''}${done ? ' done' : ''}`} key={f.clientId} title={f.note ?? f.name}>
                      {f.previewUrl ? (
                        <img alt="" src={f.previewUrl} loading="lazy" />
                      ) : (
                        <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--muted)', fontSize: 11 }}>
                          {extOf(f.name).toUpperCase()}
                        </div>
                      )}
                      <span
                        className="st"
                        style={err ? { opacity: 1, transform: 'none', background: 'var(--danger)' } : undefined}
                      >
                        <Icon name={dup || err ? 'x' : 'check'} />
                      </span>
                      <span className="nm">{f.name}</span>
                      {!dup && !done && (
                        <span className="pb">
                          <i style={{ width: `${Math.round(f.progress * 100)}%` }} />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="how">
            <div>
              <b>1 · Checked in your browser</b>We read each photo’s capture date and fingerprint, and skip duplicates
              already in Glean.
            </div>
            <div>
              <b>2 · Uploaded straight to S3</b>Parallel uploads that retry on their own. Your originals are never
              recompressed.
            </div>
            <div>
              <b>3 · Ready in seconds</b>Thumbnails are generated automatically, and the photos appear in search right
              away.
            </div>
          </div>
        </div>
        <div className="panel">
          <h3>Upload details</h3>
          <p className="help">Applied to every photo in this upload. You can change tags on individual photos later.</p>
          <div className="field">
            <label htmlFor="upAlbum">Album</label>
            <AlbumCombobox
              id="upAlbum"
              albums={uploadable}
              value={album}
              onChange={chooseAlbum}
              allowCreate={access.isMedia}
              disabled={phase !== 'idle'}
              placeholder={access.isMedia ? 'Search albums or type a new name…' : 'Search your albums…'}
            />
            {!album && <div className="help" style={{ margin: '6px 0 0' }}>Required: every photo lives in an album.</div>}
          </div>
          <div className="field">
            <label>Tags</label>
            <TagEditor
              id="upTags"
              tags={tagState.tags}
              autoTags={tagState.auto}
              albumTags={albumDefaults}
              disabled={phase !== 'idle'}
              placeholder="Add tag… (e.g. summer-retreat)"
              onChange={(tags) => setTagState((s) => ({ ...s, tags }))}
            />
            {profileTags.some((t) => !tagState.tags.includes(t)) && (
              <div className="sugg-group" id="upProfile">
                <div className="sl">From your profile</div>
                <div className="sugg">
                {profileTags
                  .filter((t) => !tagState.tags.includes(t))
                  .map((t) => (
                    <button
                      type="button"
                      key={t}
                      title={tagIndex.labelOf(t)}
                      disabled={phase !== 'idle'}
                      onClick={() => setTagState((s) => ({ ...s, tags: [...s.tags, t] }))}
                    >
                      + #{t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="sugg-group" id="upSugg">
              <div className="sl">Suggested</div>
              <div className="sugg">
              {SUGGESTED.filter((t) => !tagState.tags.includes(t))
                .slice(0, 6)
                .map((t) => (
                  <button
                    type="button"
                    key={t}
                    disabled={phase !== 'idle'}
                    onClick={() => setTagState((s) => ({ ...s, tags: [...s.tags, t] }))}
                  >
                    + #{t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="field">
            <label htmlFor="upVis">Who can see these?</label>
            <select
              className="inp"
              id="upVis"
              value={visibility}
              disabled={phase !== 'idle'}
              onChange={(e) => setVisibility(e.target.value as CreateUploadBody['visibility'])}
            >
              {(visibilities.length ? visibilities : (['unreleased'] as const)).map((v) => (
                <option key={v} value={v}>
                  {v === 'unreleased' ? 'Unreleased (only in Studio)' : 'Restricted'}
                </option>
              ))}
            </select>
            <div className="help" id="visDesc" style={{ margin: '6px 0 0' }}>
              {visibility === 'unreleased' ? 'Someone with release access releases them to everyone after review.' : VISIBILITY_LOOK.restricted.description}
            </div>
          </div>
          <button
            type="button"
            className="btn primary"
            id="go"
            style={{ width: '100%', justifyContent: 'center', height: 42 }}
            disabled={phase !== 'idle' || reading > 0 || !ready || !album}
            onClick={start}
          >
            {goLabel}
          </button>
          <p className="help" style={{ margin: '10px 0 0', textAlign: 'center' }}>
            Uploaded by <b>{user.name}</b>. Your name is shown on each photo.
          </p>
          {newAlbumName !== null && (
            <NewAlbumDialog
              initialName={newAlbumName}
              onClose={() => setNewAlbumName(null)}
              onCreated={(a) => setAlbum({ id: a.id, name: a.name })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
